import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Injectable,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Location, PopStateEvent } from '@angular/common';

import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SentryService } from './core/sentry.service';
import { FirebaseAnalyticsService } from './core/firebase-analytics.service';
import { FullScreenOverflowComponent } from './ui/full-screen-overflow/full-screen-overflow.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MaintenanceService } from './core/maintenance.service';
import { MaintenanceComponent } from './ui/maintenance/maintenance.component';


@Injectable({ providedIn: 'root' })
export class NavEventService {

  readonly navCounter$ = new BehaviorSubject<number>(0);

  navStarts() {
    this.navCounter$.next(this.navCounter$.value + 1);
  }

  navStops() {
    this.navCounter$.next(this.navCounter$.value - 1);
  }
}


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, FullScreenOverflowComponent, MaintenanceComponent]
})
export class AppComponent implements OnInit, AfterViewInit {
  private sentryService = inject(SentryService);
  private analyticsService = inject(FirebaseAnalyticsService);
  private navService = inject(NavEventService);
  private maintenanceService = inject(MaintenanceService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private location = inject(Location);
  private lastPoppedUrl: string | null = null;
  private yScrollStack: number[] = [];

  readonly underMaintenance = this.maintenanceService.isUnderMaintenance;

  readonly loading = toSignal(this.navService.navCounter$.pipe(
    map(count => count > 0),

    takeUntilDestroyed(this.destroyRef),
  ));


  ngOnInit() {
    this.sentryService.init();
    this.analyticsService.init(); 

    this.location.subscribe((ev: PopStateEvent) => {
      this.lastPoppedUrl = ev.url ?? null;
    });
  }

  ngAfterViewInit() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((ev: any) => {
        if (ev instanceof NavigationStart) {
          window.scrollTo({ top: 0, behavior: 'auto' });

          if (ev.url !== this.lastPoppedUrl) {
            this.yScrollStack.push(window.scrollY);
          }
        } else if (ev instanceof NavigationEnd) {
          this.logPageView();

          if (ev.url === this.lastPoppedUrl) {
            this.lastPoppedUrl = null;
            window.scrollTo(0, this.yScrollStack.pop() ?? 0);
          } else {
            window.scrollTo(0, 0);
          }
        }
      });
  }

  private logPageView() {
    this.analyticsService.logEvent('page_view');
  }
}
